import React, { useState, useEffect } from "react"
import { useHistory, useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { CommonAttributes, IDayProgram } from "eteva-types"
import { makeStyles } from "@material-ui/core"
import { Screen, Loader, Form } from "../../components"
import { useStores } from "../../models/root-store"
import { FormSection, FormSelectField } from "../../components/Form/FormTypes"
import { DayProgramWithPreview } from "../../utils/interfaces"
import { formatFormData } from "../../utils"
// import { ModificationDetails } from "../../components/ModificationDetails"
import { RootPaths } from "../../routers/Paths"

const useStyles = makeStyles({
  container: {
    padding: 10,
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    "@media (min-width:600px)": {
      padding: 50,
      display: "flex",
      flex: 1,
    },
  },
})

interface DayProgramScreenProps {}

export const DayProgramScreen: React.FC<DayProgramScreenProps> = observer(() => {
  const {
    dayProgramStore: { fetching, getOne, create, update, submitSections, clearCurrentSections },
    categoryStore: { fetching: fetchingCategories, getAll: getCategories, asSelectOptions: categories },
    subCategoryStore: { fetching: fetchingSubCategories, getAll: getSubCategories, asSelectOptions: subCategories },
  } = useStores()
  const [dayProgramState, setDayProgramState] = useState<Partial<DayProgramWithPreview & CommonAttributes>>({})
  const { container } = useStyles()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()

  const getDayProgramWithId = async (id: number) => {
    const newDayProgram: IDayProgram = await getOne(id)
    if (newDayProgram) {
      setDayProgramState({
        ...newDayProgram,
        categories: newDayProgram.categories?.map(it => it.id) || [],
        subCategories: newDayProgram.subCategories?.map(it => it.id) || [],
      })
    }
  }

  useEffect(() => {
    return () => {
      clearCurrentSections()
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      await getCategories(0, 1000)
      await getSubCategories(0, 1000)
      if (id && !isNaN(Number(id))) return await getDayProgramWithId(Number(id))
      setDayProgramState({})
    })()
  }, [id])

  const handleSubmit = async (state: DayProgramWithPreview) => {
    const submitForm = new FormData()
    const formattedState = {
      ...state,
      categories: state.categories?.map(it => ({
        id: it,
      })),
      subCategories: state.subCategories?.map(it => ({
        id: it,
      })),
    }

    delete formattedState.sections

    const formData = { ...formatFormData(formattedState) }
    submitForm.append("data", JSON.stringify(formData))
    if (state.imageFile) submitForm.append("image", state.imageFile)

    let success = false
    let newDayProgramId = undefined
    if (state.id) {
      success = await update(submitForm)
    } else {
      newDayProgramId = await create(submitForm)
      success = Boolean(newDayProgramId)
    }

    console.log(success)

    if (success && state.sections) {
      const doubleSuccess = await submitSections(state.sections, state.id || newDayProgramId)
      console.log(doubleSuccess)
      if (doubleSuccess) history.push(RootPaths.dayPrograms)
    }
  }

  const handleCancel = () => history.push(RootPaths.dayPrograms)

  const header = id ? "dayPrograms.edit" : "dayPrograms.add"

  const getFormFields = (type: string, title: string) =>
    ({
      type,
      title,
      fields: [
        {
          id: "title",
          type: "input",
          label: "form.title",
          required: true,
        },
        { id: "sortOrder", type: "order", label: "form.sortOrder" },
        {
          id: "imageFile",
          type: "image",
          label: "form.image",
          required: true,
        },
        {
          id: "categories",
          type: "select",
          label: "form.categories",
          options: categories(),
          multiple: true,
          loading: fetchingCategories,
          required: true,
        } as FormSelectField,
        {
          id: "subCategories",
          type: "select",
          label: "form.subCategories",
          options: subCategories(),
          multiple: true,
          loading: fetchingSubCategories,
          required: false,
        } as FormSelectField,
        {
          id: "help",
          type: "help",
          label: "form.help",
        },
        {
          id: "content",
          type: "wysiwyg",
          label: "form.content",
        },
        {
          id: "sections",
          type: "dayProgramSection",
          label: "form.dayProgramSections",
          required: true,
        },
        {
          id: "submit",
          type: "submit",
          label: "submit",
          addLabel: "form.save",
          updateLabel: "form.update",
        },
      ],
    } as FormSection)

  const renderDayProgram = () => {
    if (fetching) return <Loader />
    return (
      <div className={container}>
        <Form
          content={dayProgramState}
          formId="dayPrograms"
          section={getFormFields("dayPrograms", "")}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
        {/* <ModificationDetails item={dayProgramState} /> */}
      </div>
    )
  }
  return <Screen headerTitle={header}>{renderDayProgram()}</Screen>
})
